import { Button, Icon, Input, Spin } from 'antd';
import * as React from 'react';
import ReactHtmlParser from 'react-html-parser';
import injectSheet from 'react-jss';
import { fetchAllComments, fetchTaggableUsers, postComment } from 'src/api/comments';
import { CommentApiPrefix } from 'src/api/routes';
import CommentIcon from 'src/components/common/icons/CommentIcon';
import SortIcon from 'src/components/common/icons/SortIcon';
import DropdownSelect from '../DropdownSelect';
import InputWithMentions from '../InputWithMentions';
import CommentCard from './CommentCard';

const styles = theme => ({
  blueButton: {
    borderRadius: '4px',
    background: '#c2ddfa',
  },
  menuItem: {
    '& .ant-menu-item': {
      marginBottom: '2px',
      marginTop: '0px',
      height : '28px !important',
    },
  },
  clickable: {
    color: theme.colors.textSecondary,
    margin: theme.spacing.sm,
    cursor: 'pointer',
  },
  commentAdd: {
    height: '61px',
    borderBottom: `1px solid ${theme.colors.border}`,
    display: 'flex',
    justifyContent: 'space-around',
  },
  commentFilters: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '65%',
    marginTop: '15px',
    marginBottom: '40px',
  },
  comment : {
    width: '100%',
    backgroundColor: theme.colors.bgWhite,
  },
  commentSearchInput: {
    width: '228px',
    marginRight: '2%',
    '& .ant-input-suffix': {
      zIndex: 0,
    },
  },
  noComment : {
    width: '100%',
    display: 'flex',
    height: '250px',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.bgWhite,
  },
});
interface ConversationCommentsProps {
  sourceObjectId: any;
  queryString: any;
  commentApiPrefix: CommentApiPrefix;
  onCommentPostedCallback?: any;
  theme: any;
  classes: any;
  searchApi?: any;
}
class ConversationComments extends React.PureComponent<ConversationCommentsProps, any> {
  state : any = {
    isComment : true,
    clickCoord: {},
    isLoading: true,
    commentData: [],
    userList: [],
    isCommenting: false,
    userListFilter: [],
    loadingMoreComments: false,
    commentInput: '',
    commentInputKey: 0,
    searchQuery: '',
    sortBy: 'updated_at',
    descendingOrder: true,
    pagination: {
      nextOrPrev: 'first',
      lastSortedColumnValue: undefined,
      lastRowId: '',
      currentPageNumber: 1,
      resultPerPage: 10,
      isNextPresent: false,
      lastItemIdNext: undefined,
      lastItemIdPrev: undefined,
      lastSortedColumnValueNext: undefined,
      lastSortedColumnValuePrev: undefined,
    },
  };

  componentDidMount = async () => {
    const {
      sourceObjectId,
      queryString,
      searchApi,
      commentApiPrefix,
    } = this.props;
    let response;
    if (searchApi) {
      response = await searchApi(sourceObjectId);
    } else {
      response = await fetchTaggableUsers({
        sourceObjectId,
        queryString,
      }, commentApiPrefix);
    }
    if (response.isSuccess) {
      this.setState({ userList: response.data || [] },
        async () => await this.fetchAllCommentsFunction());
    }
  }

  resetPagination = () => {
    this.setState({
      pagination: {
        nextOrPrev: 'first',
        lastSortedColumnValue: undefined,
        lastRowId: '',
        currentPageNumber: 1,
        resultPerPage: 10,
        isNextPresent: false,
        lastItemIdNext: undefined,
        lastItemIdPrev: undefined,
        lastSortedColumnValueNext: undefined,
        lastSortedColumnValuePrev: undefined,
      },
    }, async() => await this.fetchAllCommentsFunction());
  }

  fetchAllCommentsFunction = async() => {
    const { sourceObjectId, commentApiPrefix } = this.props;

    const body: any = {};
    body.sourceObjectId = sourceObjectId;
    body.parentId = null;
    body.userList = this.state.userListFilter;
    body.searchQuery = this.state.searchQuery;
    body.descendingOrder = this.state.descendingOrder;
    body.nextOrPrev = this.state.pagination.nextOrPrev;
    body.lastSortedColumnValue = this.state.pagination.lastSortedColumnValue;
    body.sortBy = this.state.sortBy;
    body.lastRowId = this.state.pagination.lastRowId;
    body.currentPageNumber = this.state.pagination.currentPageNumber;
    body.resultPerPage = this.state.pagination.resultPerPage;
    this.setState({ isLoading: true });
    const response = await fetchAllComments(body, commentApiPrefix);
    if (response.isSuccess) {
      this.setState({ commentData: response.response.data,
        pagination: {
          ...this.state.pagination,
          currentPageNumber: response.response.currentPageNumber,
          isNextPresent: response.response.isNextPresent,
          lastItemIdNext: response.response.lastItemIdNext,
          lastItemIdPrev: response.response.lastItemIdPrev,
          lastSortedColumnValueNext: response.response.lastSortedColumnValueNext,
          lastSortedColumnValuePrev: response.response.lastSortedColumnValuePrev,
        } });
    }
    this.setState({ isLoading: false });
  }

  getTextFromHtml = (html) => {
    let text = '';
    ReactHtmlParser(html, {
      transform: (node, index) => {
        if (node.type === 'text') {
          text = text.concat(node.data);
        }
      },
    });
    return text;
  }

  handleComment = async() => {
    const { sourceObjectId } = this.props;
    const { commentInput: commentInputRaw, commentInputKey } = this.state;
    const commentInput = commentInputRaw.trim();
    if (!commentInput || !commentInput.length) return ;
    this.setState({
      isCommenting: true,
    });
    const body: any = {};
    body.sourceObjectId = sourceObjectId;
    body.parentId = null;
    body.rawHtml = commentInput;
    body.text = this.getTextFromHtml(commentInput);

    await postComment(body, this.props.commentApiPrefix);
    this.resetPagination();
    this.setState({
      commentInput: '', commentInputKey: commentInputKey + 1,
      isCommenting: false,
    });
    if (typeof this.props.onCommentPostedCallback === 'function') {
      this.props.onCommentPostedCallback();
    }
  }

  renderNoComments = () => {
    return <div className={this.props.classes.noComment}>
        <div>
          <CommentIcon style={{ width: '40px', height: '40px' }} />
        </div>
        <div style={{ fontSize: '18px', fontWeight: 600 }}>
          No comments made so far!
        </div>
      </div>;
  }

  resetSearch = () => {
    this.setState({ searchQuery: '' }, async() => await this.fetchAllCommentsFunction());
  }

  onSearch = (e) => {
    this.setState({ searchQuery: e.target.value },
      async() => await this.fetchAllCommentsFunction());
  }

  handleCommentUpdate = (commentText) => {
    this.setState({ commentInput: commentText });
  }

  showMoreComments = () => {
    const { sourceObjectId, commentApiPrefix } = this.props;
    const { pagination } = this.state;
    const { lastItemIdNext: lastRowId,
      lastSortedColumnValueNext: lastSortedColumnValue } = pagination;
    this.setState({
      pagination : {
        ...this.state.pagination,
        lastRowId,
        lastSortedColumnValue,
        nextOrPrev: 'next',
      },
    }, async () => {
      const body: any = {};
      body.sourceObjectId = sourceObjectId;
      body.parentId = null;
      body.userList = this.state.userListFilter;
      body.searchQuery = this.state.searchQuery;
      body.descendingOrder = this.state.descendingOrder;
      body.nextOrPrev = this.state.pagination.nextOrPrev;
      body.lastSortedColumnValue = this.state.pagination.lastSortedColumnValue;
      body.sortBy = this.state.sortBy;
      body.lastRowId = this.state.pagination.lastRowId;
      body.currentPageNumber = this.state.pagination.currentPageNumber;
      body.resultPerPage = this.state.pagination.resultPerPage;
      this.setState({ loadingMoreComments: true });
      const response = await fetchAllComments(body, commentApiPrefix);
      const newCommentData: any = this.state.commentData;
      if (response.isSuccess) {
        this.setState({ commentData: newCommentData.concat(response.response.data),
          pagination: {
            ...this.state.pagination,
            currentPageNumber: response.response.currentPageNumber,
            isNextPresent: response.response.isNextPresent,
            lastItemIdNext: response.response.lastItemIdNext,
            lastItemIdPrev: response.response.lastItemIdPrev,
            lastSortedColumnValueNext: response.response.lastSortedColumnValueNext,
            lastSortedColumnValuePrev: response.response.lastSortedColumnValuePrev,
          } });
      }
      this.setState({ loadingMoreComments: false });
    });
  }

  showMore = () => {
    const { isLoading, loadingMoreComments, pagination, clickCoord } = this.state;
    const isNextPresent = pagination && pagination.isNextPresent;
    if (!isNextPresent) return <></>;
    if (loadingMoreComments) {
      return <div style={{ marginLeft: '158px' }}>
        <Spin size="small" />
      </div>;
    }
    return <div style={{ marginLeft: '158px', color: this.props.theme.colors.primary,
      fontSize: this.props.theme.fontSize.body, cursor: 'pointer' }}
      onClick={this.showMoreComments}>
      <Icon style={{ color: this.props.theme.colors.primary, marginRight: '2px', marginTop: '15px' }}
      type="down"/>Show More
    </div>;
  }

  handleUserFilterChange = async (newUserList) => {
    await this.setState({ userListFilter: newUserList });
    this.fetchAllCommentsFunction();
  }

  renderClearFilters = () => {
    const { classes } = this.props;
    return this.state.userListFilter.length > 0 ?
    <div className={classes.childToolbar}
      onClick={() => this.handleUserFilterChange([])}
     style={{
      //  marginTop: '8px',
       color: this.props.theme.primaryColor, fontWeight: 600,
       fontSize: '13px', cursor: 'pointer', marginLeft: '10px', lineHeight: '20px'}}>
      Clear Filters
    </div> : null;
  }

  render () {
    const { userList, commentInputKey, isLoading, userListFilter,
      isCommenting, commentInput } = this.state;
    const { classes } = this.props;
    const disableCommentButton = isCommenting || !commentInput;
    const userOptionsToShow = userList && userList.map(item => ({
      key: item.id,
      label: `${item.name} (${item.organisationName})`,
      details: JSON.stringify(item),
    }));
    return <div className={classes.comment}>
      <div className={classes.commentAdd} style={{ paddingTop: '15px' }}>
        <InputWithMentions
          key={commentInputKey}
          onChange={this.handleCommentUpdate}
          suggestions={[{
            trigger: '@',
            type: 'user',
            list: userList && userList.map((user) => {
              return {
                id: user.id,
                display: `${user.name} (${user.organisationName})`,
                data: user,
              };
            }),
          },
          ]} />
        <Button type="primary" style={{
          marginTop: '2px',
          backgroundColor: this.props.theme.colors.primary,
          border: this.props.theme.colors.primary,
          borderRadius: this.props.theme.radius.md,
        }} onClick={this.handleComment}
          disabled={disableCommentButton}
        >Comment</Button>
      </div>
    <div className={classes.commentFilters} style={{ paddingLeft: '15px' }}>
    <Input
      suffix={this.state.searchQuery &&
        this.state.searchQuery.length ? <Icon onClick={this.resetSearch}
        type="cross"/> : <Icon type="search" />}
      placeholder="Search"
      onPressEnter={this.onSearch}
      onChange={this.onSearch}
      value={this.state.searchQuery}
      className={classes.commentSearchInput} />
    <span style={{
      marginRight: '2%',
    }}>
      <DropdownSelect
        searchVisible={true}
        itemList={userListFilter}
        options={userOptionsToShow}
        label={'All Users'}
        onChange={this.handleUserFilterChange}
      />
    </span>
    <span style={{ /*marginTop: '8px'*/ }}><a style={{ color: this.props.theme.colors.textSecondary }} onClick={() =>
      this.setState({ descendingOrder: !this.state.descendingOrder },
      async() => await this.fetchAllCommentsFunction())}>
      <SortIcon /> Sort: {!this.state.descendingOrder ? 'Oldest to Newest' :
    'Newest to Oldest'}</a></span>
    {this.renderClearFilters()}
    </div>
    <div>
        {
          isLoading ?
            <div style={{
              width: '100%', height: '150px',
              display: 'flex', justifyContent: 'center', alignItems: 'center',
            }}>
              <Spin size="large" />
            </div>
            : this.state.commentData.length === 0 ?
              this.renderNoComments()
              : this.state.commentData.map((item, index) =>
                <CommentCard
                  data={this.state.commentData[index]}
                  userList={userList} />)
        }
      </div>
    {this.showMore()}
  </div>;
  }
}
export default injectSheet(styles)(ConversationComments);
